import { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Check, Loader2, Play } from 'lucide-react';
import '../../styles/v2/pages/investigation-wizard.css';

const STEPS = [
  { id: 1, label: 'Choose report' },
  { id: 2, label: 'Configure' },
  { id: 3, label: 'Delivery' },
];

const DATE_RANGE_OPTIONS = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

const DEFAULT_CONFIG_PARAMS = {
  dateRange: '7d',
  format: 'PDF',
  includeCharts: true,
  groupBy: 'deviceType',
  deliveryMode: 'now',
  frequency: 'WEEKLY',
  timeOfDay: '08:00',
  dayOfWeek: 1,
  dayOfMonth: 1,
  recipientsInput: '',
  severity: [],
  conditionType: [],
  createFile: false,
  saveAsTemplate: false,
  scheduleName: '',
  templateName: '',
};

const CHART_SUPPORTED_TYPES = ['SYSTEM_HEALTH', 'USER_ACTIVITY', 'ACCESS_CONTROL_USERS', 'EXPIRING_CARDS', 'REGISTRATIONS_BY_DATE', 'DEVICE_MONITOR_EVENTS'];

// ─────────────────────────────────────────────
// STEP 1: CHOOSE REPORT
// ─────────────────────────────────────────────

function ChooseReportStep({ module, reportTypes, onSelect }) {
  const available = module.reportIds
    .map(id => reportTypes.find(rt => rt.id === id))
    .filter(Boolean);

  return (
    <div className="wizard-step">
      <div className="wizard-step-intro">
        <h3>Choose a report</h3>
        <p>Pick a report from {module.name} to configure and generate.</p>
      </div>

      <div className="wizard-device-section">
        <div className="wizard-device-section-header">
          <div className="wizard-device-section-title">
            <span>Available reports</span>
          </div>
        </div>
        {available.length === 0 ? (
          <p className="wizard-device-empty">No reports available yet in this module.</p>
        ) : (
          <div className="wizard-device-list">
            {available.map(rt => (
              <button
                key={rt.id}
                type="button"
                className={`wizard-device-item ${!rt.available ? 'disabled' : ''}`}
                onClick={() => rt.available && onSelect(rt)}
                disabled={!rt.available}
              >
                <div className="wizard-device-check">
                  <Check size={10} style={{ visibility: 'hidden' }} />
                </div>
                <div className="wizard-device-info">
                  <span className="wizard-device-name">{rt.name}</span>
                  <span className="wizard-device-sub">
                    {rt.available ? rt.description : rt.unavailableReason}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {module.comingSoon.length > 0 && (
        <div className="wizard-device-section">
          <div className="wizard-device-section-header">
            <div className="wizard-device-section-title">
              <span>Coming soon</span>
            </div>
          </div>
          <div className="wizard-device-list">
            {module.comingSoon.map(cs => (
              <div key={cs.name} className="wizard-device-item disabled">
                <div className="wizard-device-check">
                  <Check size={10} style={{ visibility: 'hidden' }} />
                </div>
                <div className="wizard-device-info">
                  <span className="wizard-device-name">{cs.name}</span>
                  <span className="wizard-device-sub">{cs.description}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// STEP 2: CONFIGURE
// ─────────────────────────────────────────────

function ConfigureStep({ selectedType, params, setParams }) {
  const updateParam = (key, value) => setParams(prev => ({ ...prev, [key]: value }));

  return (
    <div className="wizard-step">
      <div className="wizard-step-intro">
        <h3>Configure your report</h3>
        <p>Set the parameters, then generate.</p>
      </div>

      {selectedType?.id === 'REGISTRATIONS_BY_DATE' ? (
        <div className="wizard-row-2">
          <div className="wizard-field">
            <label className="wizard-label">Start date</label>
            <input className="wizard-input" type="date"
              value={params.customStartDate || ''}
              onChange={e => updateParam('customStartDate', e.target.value)} />
          </div>
          <div className="wizard-field">
            <label className="wizard-label">End date</label>
            <input className="wizard-input" type="date"
              value={params.customEndDate || ''}
              onChange={e => updateParam('customEndDate', e.target.value)} />
          </div>
        </div>
      ) : (
        <div className="wizard-field">
          <label className="wizard-label">Date range</label>
          <select className="wizard-input" value={params.dateRange} onChange={e => updateParam('dateRange', e.target.value)}>
            {DATE_RANGE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      )}

      {CHART_SUPPORTED_TYPES.includes(selectedType?.id) && (
        <div className="wizard-field">
          <label className="wizard-label">Include charts</label>
          <select className="wizard-input" value={params.includeCharts ? 'yes' : 'no'} onChange={e => updateParam('includeCharts', e.target.value === 'yes')}>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
      )}

      {selectedType?.id === 'EXPIRING_CARDS' && (
        <div className="wizard-field">
          <label className="wizard-label">Expiring within (days)</label>
          <input className="wizard-input" type="number" min={1} max={365}
            value={params.thresholdDays || 30}
            onChange={e => updateParam('thresholdDays', Number(e.target.value))} />
          <span className="wizard-hint">Cards expiring within this many days (or already expired) will be included.</span>
        </div>
      )}

      {selectedType?.id === 'SYSTEM_HEALTH' && (
        <div className="wizard-field">
          <label className="wizard-label">Group by</label>
          <select className="wizard-input" value={params.groupBy || 'deviceType'} onChange={e => updateParam('groupBy', e.target.value)}>
            <option value="deviceType">Device type</option>
            <option value="site">Site</option>
            <option value="none">No grouping</option>
          </select>
        </div>
      )}

      {selectedType?.id === 'DEVICE_MONITOR_EVENTS' && (
        <>
          <div className="wizard-field">
            <label className="wizard-label">Severity</label>
            <div className="wizard-window-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              {['INFO', 'WARNING', 'CRITICAL'].map(sev => (
                <button key={sev} type="button"
                  className={`wizard-window-btn ${(params.severity || []).includes(sev) ? 'active' : ''}`}
                  onClick={() => {
                    const current = params.severity || [];
                    const next = current.includes(sev) ? current.filter(s => s !== sev) : [...current, sev];
                    updateParam('severity', next);
                  }}>{sev}</button>
              ))}
            </div>
          </div>
          <div className="wizard-field">
            <label className="wizard-label">Condition type</label>
            <div className="wizard-window-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              {['DEVICE_OFFLINE', 'IP_CHANGE', 'LATENCY_THRESHOLD'].map(ct => (
                <button key={ct} type="button"
                  className={`wizard-window-btn ${(params.conditionType || []).includes(ct) ? 'active' : ''}`}
                  onClick={() => {
                    const current = params.conditionType || [];
                    const next = current.includes(ct) ? current.filter(c => c !== ct) : [...current, ct];
                    updateParam('conditionType', next);
                  }}>{ct.replace(/_/g, ' ')}</button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// STEP 3: DELIVERY
// ─────────────────────────────────────────────

function DeliveryStep({ selectedType, params, setParams }) {
  const updateParam = (key, value) => setParams(prev => ({ ...prev, [key]: value }));
  const mode = params.deliveryMode || 'now';

  return (
    <div className="wizard-step">
      <div className="wizard-step-intro">
        <h3>Delivery</h3>
        <p>Choose when this report should run, and optionally save it as a template.</p>
      </div>

      <div className="wizard-field">
        <label className="wizard-label">When</label>
        <div className="wizard-window-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <button type="button" className={`wizard-window-btn ${mode === 'now' ? 'active' : ''}`}
            onClick={() => updateParam('deliveryMode', 'now')}>Run once now</button>
          <button type="button" className={`wizard-window-btn ${mode === 'schedule' ? 'active' : ''}`}
            onClick={() => updateParam('deliveryMode', 'schedule')}>Schedule recurring</button>
        </div>
      </div>

      <div className="wizard-field">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input type="checkbox" id="createFile" checked={!!params.createFile}
            onChange={e => updateParam('createFile', e.target.checked)} />
          <label htmlFor="createFile" className="wizard-label" style={{ margin: 0 }}>Generate a file I can download</label>
        </div>
        <span className="wizard-hint">Otherwise the report is available to view in-app, and you can still download it later from the Generated tab.</span>
      </div>

      {params.createFile && (
        <div className="wizard-field">
          <label className="wizard-label">Output format</label>
          <select className="wizard-input" value={params.format}
            onChange={e => updateParam('format', e.target.value)}>
            <option value="PDF">PDF</option>
            <option value="XLSX">Excel (XLSX)</option>
          </select>
        </div>
      )}

      {mode === 'schedule' && (
        <>
          <div className="wizard-field">
            <label className="wizard-label">Schedule name <span className="wizard-req">*</span></label>
            <input className="wizard-input" type="text" placeholder="e.g. Weekly access review"
              value={params.scheduleName || ''} onChange={e => updateParam('scheduleName', e.target.value)} />
          </div>

          <div className="wizard-row-2">
            <div className="wizard-field">
              <label className="wizard-label">Frequency</label>
              <select className="wizard-input" value={params.frequency || 'WEEKLY'}
                onChange={e => updateParam('frequency', e.target.value)}>
                <option value="DAILY">Daily</option>
                <option value="WEEKLY">Weekly</option>
                <option value="BIWEEKLY">Biweekly</option>
                <option value="MONTHLY">Monthly</option>
              </select>
            </div>
            <div className="wizard-field">
              <label className="wizard-label">Time of day</label>
              <input className="wizard-input" type="time" value={params.timeOfDay || '08:00'}
                onChange={e => updateParam('timeOfDay', e.target.value)} />
            </div>
          </div>

          {(params.frequency === 'WEEKLY' || params.frequency === 'BIWEEKLY' || !params.frequency) && (
            <div className="wizard-field">
              <label className="wizard-label">Day of week</label>
              <select className="wizard-input" value={params.dayOfWeek ?? 1}
                onChange={e => updateParam('dayOfWeek', Number(e.target.value))}>
                <option value={0}>Sunday</option>
                <option value={1}>Monday</option>
                <option value={2}>Tuesday</option>
                <option value={3}>Wednesday</option>
                <option value={4}>Thursday</option>
                <option value={5}>Friday</option>
                <option value={6}>Saturday</option>
              </select>
            </div>
          )}

          {params.frequency === 'MONTHLY' && (
            <div className="wizard-field">
              <label className="wizard-label">Day of month</label>
              <input className="wizard-input" type="number" min={1} max={28} value={params.dayOfMonth || 1}
                onChange={e => updateParam('dayOfMonth', Number(e.target.value))} />
              <span className="wizard-hint">Use 1-28 to avoid issues with shorter months.</span>
            </div>
          )}

          <div className="wizard-field">
            <label className="wizard-label">Email recipients <span className="wizard-optional">(optional)</span></label>
            <input className="wizard-input" type="text" placeholder="email1@example.com, email2@example.com"
              value={params.recipientsInput || ''} onChange={e => updateParam('recipientsInput', e.target.value)} />
            <span className="wizard-hint">Comma-separated. They'll be emailed when each scheduled report completes.</span>
          </div>
        </>
      )}

      <div className="wizard-field">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input type="checkbox" id="saveAsTemplate" checked={!!params.saveAsTemplate}
            onChange={e => updateParam('saveAsTemplate', e.target.checked)} />
          <label htmlFor="saveAsTemplate" className="wizard-label" style={{ margin: 0 }}>Save as template</label>
        </div>
        {params.saveAsTemplate && (
          <input className="wizard-input" type="text" placeholder="Template name" style={{ marginTop: '0.5rem' }}
            value={params.templateName || ''} onChange={e => updateParam('templateName', e.target.value)} />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN WIZARD
// ─────────────────────────────────────────────

export default function ReportPickerWizard({ isOpen, module, reportTypes, generating, onClose, onSelectAndConfigure, onCreateSchedule }) {
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [configParams, setConfigParams] = useState(DEFAULT_CONFIG_PARAMS);
  const [submitError, setSubmitError] = useState('');

  // Reset on open (and when the module changes)
  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setSelectedType(null);
    setConfigParams(DEFAULT_CONFIG_PARAMS);
    setSubmitError('');
  }, [isOpen, module]);

  if (!isOpen || !module) return null;

  const handleSelect = (rt) => {
    setSelectedType(rt);
    if (rt.id === 'REGISTRATIONS_BY_DATE') {
      const today = new Date();
      const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
      const toDateInput = (d) => d.toISOString().split('T')[0];
      setConfigParams({
        ...DEFAULT_CONFIG_PARAMS,
        dateRange: 'custom',
        customStartDate: toDateInput(thirtyDaysAgo),
        customEndDate: toDateInput(today),
      });
    } else {
      setConfigParams(DEFAULT_CONFIG_PARAMS);
    }
    setStep(2);
  };

  const handleBack = () => setStep(s => s - 1);

  const handleNext = () => setStep(s => s + 1);

  const validateSubmit = () => {
    if (configParams.deliveryMode === 'schedule' && !configParams.scheduleName) {
      setSubmitError('Schedule name is required');
      return false;
    }
    if (configParams.saveAsTemplate && !configParams.templateName) {
      setSubmitError('Template name is required');
      return false;
    }
    setSubmitError('');
    return true;
  };

  const handleSubmit = () => {
    if (!selectedType) return;
    if (!validateSubmit()) return;
    if (configParams.deliveryMode === 'schedule') {
      onCreateSchedule(selectedType, configParams);
    } else {
      onSelectAndConfigure(selectedType, configParams, configParams.format);
    }
  };

  const isLastStep = step === STEPS.length;
  const isScheduleMode = configParams.deliveryMode === 'schedule';

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && !generating && onClose()}>
      <div className="wizard-modal">

        {/* Header */}
        <div className="wizard-header">
          <div className="wizard-header-title">
            <span>{module.name} reports</span>
          </div>
          <button className="wizard-close" onClick={onClose} disabled={generating}><X size={16} /></button>
        </div>

        {/* Progress */}
        <div className="wizard-progress">
          {STEPS.map((s, i) => (
            <div key={s.id} className={`wizard-progress-step ${step === s.id ? 'active' : step > s.id ? 'done' : ''}`}>
              <div className="wizard-progress-dot">
                {step > s.id ? <Check size={10} /> : s.id}
              </div>
              <span className="wizard-progress-label">{s.label}</span>
              {i < STEPS.length - 1 && <div className="wizard-progress-line" />}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="wizard-body">
          {step === 1 && (
            <ChooseReportStep module={module} reportTypes={reportTypes} onSelect={handleSelect} />
          )}
          {step === 2 && (
            <ConfigureStep selectedType={selectedType} params={configParams} setParams={setConfigParams} />
          )}
          {step === 3 && (
            <DeliveryStep selectedType={selectedType} params={configParams} setParams={setConfigParams} />
          )}
          {submitError && <div className="wizard-step"><span className="wizard-err-msg">{submitError}</span></div>}
        </div>

        {/* Footer */}
        <div className="wizard-footer">
          <button className="btn-secondary" onClick={step === 1 ? onClose : handleBack} disabled={generating}>
            {step === 1 ? 'Cancel' : <><ChevronLeft size={14} /> Back</>}
          </button>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span className="wizard-step-indicator">Step {step} of {STEPS.length}</span>
            {!isLastStep && step > 1 && (
              <button className="btn-primary" onClick={handleNext}>
                Next <ChevronRight size={14} />
              </button>
            )}
            {isLastStep && (
              <button className="btn-primary" onClick={handleSubmit} disabled={generating || !selectedType}>
                {generating ? (
                  <><Loader2 size={13} className="spin" /> {isScheduleMode ? 'Creating...' : 'Generating...'}</>
                ) : isScheduleMode ? (
                  <><Check size={13} /> Create schedule</>
                ) : (
                  <><Play size={13} /> Generate</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
